import { useState, useEffect } from "react";
import { api, API } from "../lib/api";
import { Loader2, Lock, Download } from "lucide-react";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("bh_admin") || "");
  const [pw, setPw] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const login = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/admin/login", { password: pw });
      localStorage.setItem("bh_admin", data.token); setToken(data.token);
    } catch (e) { setErr("Nieprawidłowe hasło"); }
    setLoading(false);
  };

  useEffect(() => {
    if (!token) return;
    api.get("/admin/orders", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setOrders(r.data))
      .catch(() => { localStorage.removeItem("bh_admin"); setToken(""); });
  }, [token]);

  const STATES = ["nowe", "w realizacji", "wysłane", "zrealizowane"];
  const [fPay, setFPay] = useState("all");
  const [fFul, setFFul] = useState("all");
  const [track, setTrack] = useState({});
  const getTrack = (o) => (track[o.id] !== undefined ? track[o.id] : (o.tracking || ""));
  const updateStatus = async (o, status) => {
    const tracking = getTrack(o);
    setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, fulfillment_status: status, tracking } : x)));
    try {
      await api.patch(`/admin/orders/${o.id}/status`, { fulfillment_status: status, tracking }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {}
  };

  const filtered = orders.filter((o) =>
    (fPay === "all" || (o.payment_status || "pending") === fPay) &&
    (fFul === "all" || (o.fulfillment_status || "nowe") === fFul)
  );

  const PRICES = { standard: 249, plus: 299, premium: 349, wspolna: 399, wielka: 590 };
  const paid = orders.filter((o) => o.payment_status === "paid");
  const productRev = paid.reduce((s, o) => s + (PRICES[o.package_id] || 0), 0);
  const shipRev = paid.reduce((s, o) => s + (o.delivery?.cost || 0), 0);
  const grandRev = productRev + shipRev;

  const exportCSV = () => {
    const cols = [
      ["Data", (o) => o.created_at], ["Pakiet", (o) => o.package_id], ["Płatność", (o) => o.payment_status],
      ["Realizacja", (o) => o.fulfillment_status || "nowe"], ["Nr przesyłki", (o) => o.tracking], ["Dziecko", (o) => o.child?.name], ["Wiek", (o) => o.child?.age],
      ["Świat", (o) => `${o.world?.theme || ""} ${o.world?.custom || ""}`.trim()], ["Morał", (o) => o.moral?.value],
      ["Rodzic", (o) => o.parent?.name], ["Telefon", (o) => o.parent?.phone], ["E-mail", (o) => o.contact_email],
      ["Dostawa", (o) => [o.delivery?.method, o.delivery?.paczkomat, o.delivery?.recipient, o.delivery?.street, o.delivery?.postal, o.delivery?.city, o.delivery?.country].filter(Boolean).join(" ")], ["Koszt dostawy", (o) => o.delivery?.cost],
      ["Dedykacja", (o) => o.dedication], ["Notatki", (o) => o.extra_notes],
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.map((c) => c[0]).join(",")];
    filtered.forEach((o) => rows.push(cols.map((c) => esc(c[1](o))).join(",")));
    const blob = new Blob(["\uFEFF" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `bohatero-zamowienia-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-[#E7E0D4] p-8 shadow-xl">
        <Lock className="text-[#D4AF37]" size={28} />
        <h1 className="font-display text-3xl mt-4">Panel BOHATERO</h1>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} placeholder="Hasło" data-testid="admin-password" className="mt-6 w-full rounded-2xl border border-[#E7E0D4] px-4 py-3 outline-none focus:ring-2 focus:ring-[#D4AF37]/40" />
        {err && <p className="text-red-500 text-sm mt-2">{err}</p>}
        <button onClick={login} disabled={loading} data-testid="admin-login" className="mt-4 w-full rounded-full bg-[#1A243A] text-[#FDFBF7] py-3 font-semibold hover:bg-[#D4AF37] hover:text-[#1A243A] transition-colors">{loading ? <Loader2 className="animate-spin mx-auto" size={18} /> : "Zaloguj"}</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen px-4 sm:px-10 py-10 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">Zgłoszenia ({filtered.length}/{orders.length})</h1>
        <button onClick={() => { localStorage.removeItem("bh_admin"); setToken(""); }} className="text-sm text-[#64748B] hover:text-[#1A243A]">Wyloguj</button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="revenue-summary">
        {[
          { label: "Przychód (opłacone)", value: `${grandRev} zł`, hl: true },
          { label: "Z książek", value: `${productRev} zł` },
          { label: "Z dostawy", value: `${shipRev} zł` },
          { label: "Opłaconych zamówień", value: paid.length },
        ].map((s, i) => (
          <div key={i} className={`rounded-2xl border p-5 ${s.hl ? "bg-[#1A243A] text-[#FDFBF7] border-[#D4AF37]" : "bg-white border-[#E7E0D4]"}`}>
            <p className={`text-xs uppercase tracking-widest ${s.hl ? "text-[#D4AF37]" : "text-[#64748B]"} font-bold`}>{s.label}</p>
            <p className="font-display text-3xl mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 bg-white rounded-2xl border border-[#E7E0D4] p-4">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-[#64748B] font-bold">Płatność:</span>
          {[["all", "wszystkie"], ["paid", "opłacone"], ["pending", "nieopłacone"]].map(([v, l]) => (
            <button key={v} data-testid={`filter-pay-${v}`} onClick={() => setFPay(v)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${fPay === v ? "bg-[#D4AF37] text-[#1A243A] border-[#D4AF37]" : "bg-white text-[#64748B] border-[#E7E0D4] hover:border-[#D4AF37]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-[#64748B] font-bold">Realizacja:</span>
          {[["all", "wszystkie"], ...STATES.map((s) => [s, s])].map(([v, l]) => (
            <button key={v} data-testid={`filter-ful-${String(v).replace(/\s/g, "-")}`} onClick={() => setFFul(v)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${fFul === v ? "bg-[#1A243A] text-[#FDFBF7] border-[#1A243A]" : "bg-white text-[#64748B] border-[#E7E0D4] hover:border-[#D4AF37]"}`}>{l}</button>
          ))}
        </div>
        <button onClick={exportCSV} data-testid="export-csv" className="ml-auto inline-flex items-center gap-2 rounded-full px-5 py-2 bg-[#1A243A] text-[#FDFBF7] text-sm font-semibold hover:bg-[#D4AF37] hover:text-[#1A243A] transition-colors"><Download size={15} /> Eksport CSV</button>
      </div>

      <div className="mt-8 space-y-4">
        {filtered.map((o) => (
          <div key={o.id} data-testid="admin-order-row" className="bg-white rounded-2xl border border-[#E7E0D4] p-6">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div>
                <span className="font-display text-2xl">{o.child?.name || "—"}</span>
                <span className="text-[#64748B] ml-3">{o.child?.age} lat</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide px-3 py-1 rounded-full bg-[#E6D7FF] text-[#1A243A]">{o.package_id}</span>
                <span className={`text-xs px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-[#D4AF37]/20 text-[#8a7220]" : "bg-[#F5F2EB] text-[#64748B]"}`}>{o.payment_status}</span>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-[#64748B] font-bold mr-1">Realizacja:</span>
              {STATES.map((s) => (
                <button key={s} data-testid={`status-${s.replace(/\s/g, "-")}`} onClick={() => updateStatus(o, s)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${(o.fulfillment_status || "nowe") === s ? "bg-[#1A243A] text-[#FDFBF7] border-[#1A243A]" : "bg-white text-[#64748B] border-[#E7E0D4] hover:border-[#D4AF37]"}`}>{s}</button>
              ))}
              <input value={getTrack(o)} onChange={(e) => setTrack((p) => ({ ...p, [o.id]: e.target.value }))}
                placeholder="Nr przesyłki (do e-maila)" data-testid={`tracking-${o.id}`}
                className="ml-2 text-xs rounded-full border border-[#E7E0D4] px-4 py-1.5 outline-none focus:ring-2 focus:ring-[#D4AF37]/40 w-52" />
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 mt-4 text-sm text-[#1A243A]/80">
              <p><b>Świat:</b> {o.world?.theme} {o.world?.custom}</p>
              <p><b>Morał:</b> {o.moral?.value}</p>
              <p><b>Zainteresowania:</b> {o.child?.interests}</p>
              <p><b>Zwierzak:</b> {o.pet?.name || "—"}</p>
              <p className="sm:col-span-2"><b>Dedykacja:</b> {o.dedication}</p>
              <p className="sm:col-span-2"><b>Notatki:</b> {o.extra_notes}</p>
              <p><b>Kontakt:</b> {o.contact_email}</p>
              <p><b>Rodzic:</b> {o.parent?.name} {o.parent?.phone && `· ${o.parent.phone}`}</p>
              <p className="sm:col-span-2"><b>Dostawa:</b> {o.delivery?.method || "—"}{o.delivery?.paczkomat ? ` (${o.delivery.paczkomat})` : ""}{o.delivery?.cost != null ? ` · ${o.delivery.cost} zł` : ""} — {[o.delivery?.recipient, o.delivery?.street, o.delivery?.postal, o.delivery?.city, o.delivery?.country].filter(Boolean).join(", ") || "—"}</p>
              <p className="text-[#94A3B8]">{o.created_at?.slice(0, 16).replace("T", " ")}</p>
            </div>
            {o.photos?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">Zdjęcia ({o.photos.length})</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {o.photos.map((p) => (
                    <a key={p.id} href={`${API}/files/${p.id}?auth=${token}`} target="_blank" rel="noreferrer" title={p.kind}>
                      <img src={`${API}/files/${p.id}?auth=${token}`} alt={p.kind} className="h-20 w-20 object-cover rounded-xl border border-[#E7E0D4] hover:scale-105 transition-transform" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-[#64748B]">Brak zgłoszeń.</p>}
      </div>
    </div>
  );
}
